import get from "lodash.get";
import React, { FC } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { Checkbox } from "src/components/ui/checkbox";

import RadioStep from "../../components/RadioStep";

import BannerMultiFieldList from "../BannerMultiFieldList/BannerMultiFieldList";
import VideoUploadStep from "../../components/VideoUploadStep";
import PostFrequencyStep from "src/features/SocialFeedAdverts/components/PostFrequencyStep/PostFrequencyStep";
import MultiMediaUploadStep from "src/features/SocialFeedAdverts/components/MultiMediaUploadStep/MultiMediaUploadStep";

interface Props {
	data: BannerStep;
}

const BannerStepLayout: FC<Props> = ({ data }) => {
	const { note, step, title, type, radioAnswers, fields, isStepDisabled } =
		data;

	const { control, setValue, clearErrors } = useFormContext<BannerFormState>();
	const { errors } = useFormState({ control });

	const error = get(errors, ["steps", step - 1], null);

	const bannerType = useWatch({
		name: "bannerType",
		control
	});

	const isTypeImageBanner = bannerType === "fullImageBanner";
	const isTypeFullScreenTakeoverBanner =
		bannerType === "fullScreenTakeoverBanner";
	const isTypeVideoBanner = bannerType === "videoBanner";
	const isTypeSingleBanner = bannerType === "singleBanner";
	const isTypePromotion =
		bannerType === "promotionImage" || bannerType === "promotionVideo";

	// Hide "Select Banner Type" (Internal/Sponsored) step
	if (title === "Select Banner Type") return null;

	if (isTypeImageBanner && step === 4) return null;
	if (isTypeVideoBanner && type === "imageUpload") return null;
	if (isTypeVideoBanner && step === 2) return null;
	if (
		(isTypeSingleBanner ||
			isTypeImageBanner ||
			isTypeFullScreenTakeoverBanner) &&
		type === "videoUpload"
	)
		return null;

	// Promotion
	const isTypePromotionImage = bannerType === "promotionImage";
	const isTypePromotionVideo = bannerType === "promotionVideo";

	if (isTypePromotionVideo && step === 2) return null;
	if (isTypePromotionVideo && type === "imageUpload") return null;
	if (isTypePromotionImage && type === "videoUpload") return null;

	const cutStep = isTypeImageBanner || isTypeFullScreenTakeoverBanner ? 2 : 1;
	const stepNumber = isTypeVideoBanner || isTypePromotionVideo ? 2 : 3;

	const isUploadStep =
		type === "videoUpload" ||
		type === "imageUpload" ||
		type === "multiMediaUpload";

	const renderStepNumber = () => {
		if (bannerType !== "socialFeedAdvert" && step > stepNumber) {
			return step - cutStep;
		}

		return step;
	};

	return (
		<div
			className={[
				"rounded-xl border bg-subtle shadow-sm transition-colors",
				error ? "border-red-400/60" : "border-border"
			].join(" ")}
			aria-label={`step-${step > stepNumber ? step - cutStep : step}`}
			data-testid="step"
		>
			{error && (
				<div className="relative">
					<span
						role="alert"
						className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
					>
						{type === "postFrequency" && fields && fields.length > 1
							? "Please fill out the required fields"
							: "Please fill out the required field"}
					</span>
				</div>
			)}

			<div className="p-6">
				<div className="flex items-start">
					<div className="flex-1">
						<div className="flex items-center gap-2 mb-2">
							<label
								className="font-heading text-lg font-medium text-foreground"
								htmlFor={`step-${step}`}
							>
								{title === "Button" && isTypePromotion
									? "Link (optional)"
									: title}
							</label>

							{title === "Button" && !isTypePromotion && (
								<div className="ml-auto flex items-center gap-2">
									<Checkbox
										checked={isStepDisabled}
										onCheckedChange={(checked) => {
											const disabledStep = `steps.${step - 1}.isStepDisabled`;

											clearErrors(disabledStep);
											setValue(disabledStep, !!checked);
										}}
										id="dont-include-btn"
									/>
									<label
										htmlFor="dont-include-btn"
										className="text-sm text-zinc-700 cursor-pointer"
									>
										Don&apos;t include button
									</label>
								</div>
							)}
						</div>

						<fieldset disabled={isStepDisabled}>
							<div className="flex justify-between">
								{!isUploadStep && (
									<span
										className="text-[13px] text-muted-foreground"
										aria-label="step-title"
									>
										{note}
									</span>
								)}
							</div>

							<div className="mb-4">
								{type === "radio" && radioAnswers && (
									<RadioStep stepNumber={step} stepAnswers={radioAnswers} />
								)}

								{type === "videoUpload" && (
									<VideoUploadStep stepNumber={step} />
								)}

								{type === "multiField" && (
									<BannerMultiFieldList stepNumber={step} />
								)}

								{type === "postFrequency" && (
									<PostFrequencyStep stepNumber={step} />
								)}

								{type === "multiMediaUpload" && (
									<MultiMediaUploadStep stepNumber={step} />
								)}
							</div>
						</fieldset>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BannerStepLayout;
